import Text from '@amzn/meridian/text';
import {
  SHIFT_ALLOCATION_ID_SUFFIX,
  DROPPABLE_ID_SEPARATOR,
  TEXTS,
  PLANS,
  PLAN_VERSIONS,
  LABOR_TRACKING_STATUS,
  CYCLES
} from '../utils/constants';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Tooltip from '@amzn/meridian/tooltip';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import DatePicker from '@amzn/meridian/date-picker';
import Select, { SelectOption } from '@amzn/meridian/select';
import { chainWalk, getAvailableDates, parseEpochToDateString } from '../utils/helpers';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Sheet from '@amzn/meridian/sheet';
import Button from '@amzn/meridian/button';
import AddTooltip from '../utils/AddTooltip';
import AvailableAssociates from './view/AvailableAssociates';
import LoadingView from '../handler/loadingHandler/LoadingView';
import CustomAlert from '../commonComponents/CustomAlert';
import CopyToClipboardButton from '../commonComponents/CopyToClipboardButton';
import { ProcessPathAllocation } from './view/ProcessPathAllocation';
import { DragDropContext } from 'react-beautiful-dnd';
import OnDragEndModal from './view/OnDragEndModal';
import { ShiftAllocationContext } from './context/ShiftAllocationContext';
import { useBadgeScan } from './badgeScan/useBadgeScan';
import { AssignAssociatePanel } from './view/AssignAssociatePanel';
import { isValidDragAndDrop } from '../utils/dragAndDropHelper';
import { ProcessViewLegend } from './view/ProcessViewLengend';
import BoardStatusBar from './view/BoardStatusBar';
import { getStationCode, promiseWithTimeout } from '../utils/networkUtil';
import apis from '../utils/apis';
import {
  fixMarginAndWidth,
  fixStylesOnDragEnd,
  fixStylesOnDragUpdate,
  addStylesForStaffingIssues
} from '../utils/shiftAllocation';
import ErrorSection from './view/ErrorSection';
import { TIME_WINDOW_PHASES, PROCESS_PATHS, prepareModelForBackend } from './data';
import { getPlanChangeViolations, renderShift, getOnBoardAssociateInfo } from './view/utils';
import { MOVE_ASSOCIATE } from './action/ShiftAllocationAction';
import ViolationsToggle from './view/ViolationsToggle';
import { publishLaborAllocationCountMetric, LABOR_ALLOCATION_METRICS } from '../utils/metricUtil';
import { getUserName } from '../resources/EmployeeResource';
import {
  isDroppedInSingleOptionProcess,
  moveAssociateToSingleOptionProcess,
  moveAssociateToRecommendedSpot,
  isDroppedInBench,
  moveASsociateToBench,
  moveAssociatesAllocationsToBench
} from './view/dragAndDropUtils';
import UpsertInductAndAslLinesModel from './view/UpsertInductAndAslLinesModel';
import Box from '@amzn/meridian/box';
import Modal from '@amzn/meridian/modal';
import Toaster from '@amzn/meridian/toaster';
import Alert from '@amzn/meridian/alert';
import Icon from '@amzn/meridian/icon';
import tooltipIcon from '@amzn/meridian-tokens/base/icon/info-knockout';
import RadioButton from '@amzn/meridian/radio-button';
import UpsertClustersModel from './view/UpsertClustersModel';
import moment from 'moment-timezone';

export const nodeId = getStationCode();
const NUM_DAYS_AVAILABLE_PAST = 0;
const NUM_DAYS_AVAILABLE_FUTURE = 1;
const availableDates = getAvailableDates(NUM_DAYS_AVAILABLE_PAST, NUM_DAYS_AVAILABLE_FUTURE);

function ShiftAllocation(props) {
  const [currentTab, setCurrentTab] = useState(TIME_WINDOW_PHASES.INDUCT_AND_STOW);
  const [isFCLMTrack, setIsFCLMTrack] = useState(false);
  const [isFCLMTracking, setIsFCLMTracking] = useState(false);
  const isTrackingRef = useRef(false);
  const [isPlanSaved, setIsPlanSaved] = useState(false);
  const [isMissingPreShiftPlan, setIsMissingPreShiftPlan] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [openOnDragEndModal, setOpenOnDragEndModal] = useState(false);
  const [result, setResult] = useState();
  const [provided, setProvided] = useState();
  const [checkedPhase, setCheckedPhase] = useState(TIME_WINDOW_PHASES.INDUCT_AND_STOW);

  const displayError = useCallback((errorMessage = null) => {
    if (errorMessage) {
      setErrorMsg(errorMessage);
    }
    setIsError(true);
    setIsLoading(false);
  }, []);

  const {
    shiftAllocationModel,
    shiftAllocationDispatch,
    availableAssociatesVisibility,
    setAvailableAssociatesVisibility,
    changePlanParameters,
    storedBoardAllocations,
    cachedResponse,
    setCurrPlan,
    alertMsg,
    isBoardUpdated,
    setIsBoardUpdated,
    date,
    setDate,
    adminSettings,
    pollEnrichmentOnce,
    armedScanCard,
    armedScanCardRef,
    setArmedScanCard,
    armedScanSlot,
    armedScanSlotRef,
    setArmedScanSlot
  } = useContext(ShiftAllocationContext);

  const [selectedCycle, setSelectedCycle] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedBoardAllocationKey, setSelectedBoardAllocationKey] = useState(null);

  // Scan-to-Assign: a badge scan resolves locally against associateInfo[*].badgeBarcodeId (persisted
  // on the ShiftPlan bean, so available as soon as the plan loads). Two flows:
  //   1. Card-first: a planner clicked a process card header (armedScanCard). The scan is applied as
  //      if the associate were dragged onto that card, reusing the drag path (OnDragEndModal handles
  //      process selection + violation overrides).
  //   2. Scan-first: no card armed, so the associate's assign card opens for the planner to pick a slot.
  const [scannedAssociate, setScannedAssociate] = useState(null);
  const [pendingSlotAssign, setPendingSlotAssign] = useState(null);
  const [overrideToggles, setOverrideToggles] = useState([]);

  // Transient scan feedback (badge recognized / not found). Auto-dismisses after a few seconds.
  const [scanToasts, setScanToasts] = useState([]);
  // Pending dismissal timers, cleared on unmount so a late timer can't setState on an unmounted board.
  const scanToastTimersRef = useRef([]);
  const onCloseScanToast = (id) => setScanToasts((prev) => prev.filter((t) => t.id !== id));
  const showScanToast = (message, type) => {
    const id = `scan-${Date.now()}-${Math.random()}`;
    setScanToasts((prev) => prev.concat({ id, message, type }));
    const timer = setTimeout(() => setScanToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
    scanToastTimersRef.current.push(timer);
  };
  useEffect(() => {
    return () => {
      scanToastTimersRef.current.forEach(clearTimeout);
      scanToastTimersRef.current = [];
    };
  }, []);

  const findAssociateCurrentBoardEntry = (associate) => {
    const processInfo = (shiftAllocationModel && shiftAllocationModel.processInfo) || [];
    const entry = processInfo.find((e) => (e.assignments || []).some((a) => a.resourceId === associate.alias));
    return entry ? entry.id : PROCESS_PATHS.BENCH;
  };

  // Core of a drag-drop: given a result (+ optional provided), route to bench / recommended-spot /
  // single-process assign, or open OnDragEndModal for process selection + overrides. Shared by real
  // drags (onDragEnd) and card scans so both behave identically.
  const applyAssociateDrop = (result, provided) => {
    if (!isValidDragAndDrop(result, provided)) {
      return;
    }
    const droppedInRecommended = result.destination.droppableId.includes(
      SHIFT_ALLOCATION_ID_SUFFIX.RECOMMENDED_SPOT_DROP
    );
    const singleProcessDrop = isDroppedInSingleOptionProcess(result, shiftAllocationModel['processInfo']);
    const droppedInBench = isDroppedInBench(result);
    if (droppedInBench) {
      moveASsociateToBench(result, provided, shiftAllocationDispatch);
      setIsBoardUpdated(true);
    } else if (
      getPlanChangeViolations(result, shiftAllocationModel['processInfo'], shiftAllocationModel['associateInfo'], null)
        .length > 0 ||
      (!singleProcessDrop && !droppedInRecommended)
    ) {
      setResult(result);
      setProvided(provided);
      setOpenOnDragEndModal(true);
    } else if (droppedInRecommended) {
      moveAssociateToRecommendedSpot(result, provided, shiftAllocationDispatch);
      setIsBoardUpdated(true);
    } else if (singleProcessDrop) {
      moveAssociateToSingleOptionProcess(result, singleProcessDrop, shiftAllocationDispatch);
      setIsBoardUpdated(true);
    }
  };

  const computeSlotViolations = (associate, slot) => {
    const syntheticResult = {
      draggableId: associate.alias,
      destination: { droppableId: slot.droppableId }
    };
    return getPlanChangeViolations(
      syntheticResult,
      (shiftAllocationModel && shiftAllocationModel.processInfo) || [],
      (shiftAllocationModel && shiftAllocationModel.associateInfo) || {},
      slot.process
    );
  };

  const assignAssociateToSlot = (associate, slot) => {
    const isAisle = slot.minAisle != null && slot.maxAisle != null;
    setIsBoardUpdated(true);
    shiftAllocationDispatch({
      type: MOVE_ASSOCIATE,
      data: {
        associateAlias: associate.alias,
        from: { boardEntry: findAssociateCurrentBoardEntry(associate) },
        to: {
          boardEntry: slot.boardEntry,
          processPath: slot.process,
          // Cluster (aisle) slots carry a span; the reducer expands min..max into aisles.
          ...(isAisle ? { minAisle: Number(slot.minAisle), maxAisle: Number(slot.maxAisle) } : {})
        }
      }
    });
  };

  const overridesSatisfied =
    !!pendingSlotAssign &&
    overrideToggles.length === pendingSlotAssign.violations.length &&
    overrideToggles.every(Boolean);

  const confirmOverrideAssign = () => {
    if (!pendingSlotAssign || !overridesSatisfied) {
      return;
    }
    assignAssociateToSlot(pendingSlotAssign.associate, pendingSlotAssign.slot);
    setPendingSlotAssign(null);
    setOverrideToggles([]);
  };

  const cancelOverrideAssign = () => {
    setPendingSlotAssign(null);
    setOverrideToggles([]);
  };

  // Scannable set = the associates on THIS board. associateInfo is the whole plan roster, so scanning
  // someone who belongs to another board would dispatch a move the board appears to accept and the
  // save then drops. Scoping it here means such a badge does not resolve at all, and the planner gets
  // the same "no associate on this board" message as an unknown badge.
  const onBoardAssociateInfo = useMemo(
    () => getOnBoardAssociateInfo(shiftAllocationModel.processInfo, shiftAllocationModel.associateInfo),
    [shiftAllocationModel]
  );

  // Switching cycle, shift or board replaces the board under the planner, so a target armed on the old
  // one must not survive: armedScanSlotRef would still point at a slot that is no longer on screen.
  useEffect(() => {
    if (setArmedScanCard) {
      setArmedScanCard(null);
    }
    if (setArmedScanSlot) {
      setArmedScanSlot(null);
    }
  }, [selectedCycle, selectedShift, selectedBoardAllocationKey, setArmedScanCard, setArmedScanSlot]);

  useBadgeScan({
    associateInfo: onBoardAssociateInfo,
    // Blocked only while a confirmation dialog owns the screen: by then the slot is disarmed, so a
    // stray scan would fall through and stack the assign card on top of the confirmation it belongs
    // to, orphaning the pending assignment. The assign card itself deliberately stays scannable -
    // scanning the next badge swaps it to that associate, which is how a planner works down a queue.
    enabled: !pendingSlotAssign && !openOnDragEndModal,
    targetArmed: Boolean(armedScanCard || armedScanSlot),
    onScanResolved: (associate, resourceId, barcode) => {
      showScanToast(`Scanned: ${(associate && associate.name) || resourceId}`, 'success');
      // Slot-first: a planner clicked a specific empty slot. Assign into that slot; disarm regardless.
      const slot = armedScanSlotRef && armedScanSlotRef.current;
      if (slot) {
        if (setArmedScanSlot) {
          setArmedScanSlot(null);
        }
        const violations = computeSlotViolations(associate, slot);
        if (violations.length === 0) {
          assignAssociateToSlot(associate, slot);
        } else {
          setPendingSlotAssign({ associate, slot, violations });
        }
        return;
      }
      // Card-first: a planner clicked a process card header. Apply as if dragged onto the card.
      // The card stays ARMED across scans so a planner can bulk-scan multiple associates into it;
      // it remains the scan target until they click the card header again to deselect it.
      const card = armedScanCardRef && armedScanCardRef.current;
      if (card) {
        const fromBoardEntry = findAssociateCurrentBoardEntry(associate);
        const cardDropResult = {
          draggableId: associate.alias,
          source: {
            droppableId: `${fromBoardEntry}${DROPPABLE_ID_SEPARATOR}${SHIFT_ALLOCATION_ID_SUFFIX.PROCESS_DETAIL_CARD}`,
            index: 0
          },
          destination: {
            droppableId: `${card.boardEntry}${DROPPABLE_ID_SEPARATOR}${SHIFT_ALLOCATION_ID_SUFFIX.PROCESS_DETAIL_CARD}`,
            index: 0
          }
        };
        applyAssociateDrop(cardDropResult, null);
        return;
      }
      // Scan-first (nothing armed): open the associate's assign card to pick a destination. Scanning
      // again replaces it with the new associate rather than stacking a second card.
      setScannedAssociate({ associate, resourceId, barcode });
    },
    onScanUnresolved: (barcode, meta) => {
      // eslint-disable-next-line no-console
      console.warn('[scan] unresolved barcode', barcode, meta);
      // Close any open assign card. The planner has moved on to another badge, so leaving the previous
      // associate on screen next to a failure toast would invite assigning the wrong person.
      setScannedAssociate(null);
      if (meta && meta.ambiguous) {
        // The digits match more than one associate on the board, so the scan is deliberately refused
        // rather than placing a coin-flip guess. Tell the planner to fall back to searching by name.
        showScanToast(
          `The scanned badge (${barcode}) matches more than one associate on this board. Assign them by name instead.`,
          'error'
        );
        return;
      }
      showScanToast(`No associate on this board matched the scanned badge (${barcode}).`, 'error');
    }
  });

  const setFCLMTrackingEligibility = (status) => {
    return status === LABOR_TRACKING_STATUS.ELIGIBLE;
  };

  useEffect(() => {
    if (!selectedCycle || !shiftAllocationModel.availableCycles.includes(selectedCycle)) {
      setSelectedCycle(
        shiftAllocationModel.availableCycles.find((cycle) => cycle == CYCLES.CYCLE_1) ||
          shiftAllocationModel.availableCycles[0]
      );
    }

    shiftAllocationModel.availableShiftsForThisCycle = chainWalk(
      () => shiftAllocationModel.availableCycleShiftsMap.get(selectedCycle),
      []
    ).sort((a, b) => a.start.localeCompare(b.start));

    // Get current time in the station's timezone
    const stationTimezone =
      (cachedResponse && cachedResponse.input && cachedResponse.input.nodeZoneId) || moment.tz.guess();
    const nowStation = moment().tz(stationTimezone).format('YYYY-MM-DDTHH:mm:ss');

    // Find the shift where the current station time falls between start and end
    const getShiftForCurrentTime = (shifts) => {
      if (!shifts || shifts.length === 0) return null;
      const matchingShift = shifts.find((shift) => nowStation >= shift.start && nowStation <= shift.end);
      if (matchingShift) return matchingShift;
      // If before the earliest shift, select the first
      if (nowStation < shifts[0].start) return shifts[0];
      // If after the latest shift, select the last
      return shifts[shifts.length - 1];
    };

    if (selectedShift) {
      var currentlySelectedShift = JSON.parse(selectedShift);
      if (
        shiftAllocationModel &&
        shiftAllocationModel.availableShiftsForThisCycle &&
        shiftAllocationModel.availableShiftsForThisCycle.length > 0 &&
        !shiftAllocationModel.availableShiftsForThisCycle.find(
          (shift) => shift.start == currentlySelectedShift.start && shift.end == currentlySelectedShift.end
        )
      ) {
        const shiftForCurrentTime = getShiftForCurrentTime(shiftAllocationModel.availableShiftsForThisCycle);
        setSelectedShift(JSON.stringify(shiftForCurrentTime));
      }
    } else if (
      shiftAllocationModel &&
      shiftAllocationModel.availableShiftsForThisCycle &&
      shiftAllocationModel.availableShiftsForThisCycle.length > 0
    ) {
      const shiftForCurrentTime = getShiftForCurrentTime(shiftAllocationModel.availableShiftsForThisCycle);
      setSelectedShift(JSON.stringify(shiftForCurrentTime));
    }

    if (selectedShift && selectedCycle) {
      setSelectedBoardAllocationKey(shiftAllocationModel.boardAllocationKey);
      if (shiftAllocationModel.boardAllocationKey) {
        const [cycle, phase, start, end] = shiftAllocationModel.boardAllocationKey.split('.');
        setCheckedPhase(phase);
      }
    }
  }, [
    selectedShift,
    selectedCycle,
    shiftAllocationModel.availableCycles,
    shiftAllocationModel.availableShifts,
    shiftAllocationModel.availableShiftsForThisCycle
  ]);

  useEffect(() => {
    if (shiftAllocationModel.laborTrackingStatus) {
      setIsFCLMTrack(setFCLMTrackingEligibility(shiftAllocationModel.laborTrackingStatus));
    }
  }, [shiftAllocationModel.laborTrackingStatus]);

  const onClickOpenAvailableAssociates = useCallback(() => {
    setAvailableAssociatesVisibility(true);
  });

  const handleAssociateViewButtonClick = () => {
    props.history.push('/shift-allocation-associate-view');
  };

  const onClickJobRotation = () => {
    props.history.push('/shift-rotation');
  };

  useEffect(() => {
    setTimeout(() => {
      fixMarginAndWidth();
      addStylesForStaffingIssues(shiftAllocationModel.processInfo);
    }, 1);
  }, [isLoading, currentTab, shiftAllocationModel['processInfo']]);

  useEffect(() => {
    changePlanParameters({
      cycle: selectedCycle,
      phase: currentTab,
      shift: selectedShift && JSON.parse(selectedShift)
    });
  }, [selectedCycle, currentTab, selectedShift]);

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const onClickTrackInFCLM = async () => {
    // Guard against duplicate clicks while a track request is in flight
    if (isTrackingRef.current) return;
    isTrackingRef.current = true;
    setIsFCLMTracking(true);
    setIsLoading(true);
    setLoadingMsg('Tracking assignments into FCLM, this can take some time. Please wait and do not refresh.');

    // Yield to allow React to paint the loader before proceeding
    await new Promise((resolve) => setTimeout(resolve, 0));

    let trackIntoFCLM;
    let trackingSucceeded = false;
    let requestIssued = false;
    let errorShown = false;
    // The API error callback knows the request genuinely failed, so it wins over the
    // ambiguous message below (first caller sets the text).
    const showTrackingError = (message) => {
      if (errorShown) return;
      errorShown = true;
      displayError(message);
    };
    const TRACK_FAILED_MSG = 'FCLM tracking failed, please try again';
    const TRACK_UNKNOWN_MSG =
      'FCLM tracking is taking longer than expected and may still be processing. Refresh the page to check the latest tracking status before tracking again.';

    try {
      // The tracking call itself.
      try {
        publishLaborAllocationCountMetric(LABOR_ALLOCATION_METRICS.TRACK_INTO_FCLM);

        let body = {
          planId: cachedResponse.planId
        };

        if (selectedBoardAllocationKey) {
          const [cycle, phase, start, end] = selectedBoardAllocationKey.split('.');
          body = {
            ...body,
            timeWindowAttributes: {
              cycle: cycle,
              phase: phase
            },
            shiftTimeWindow: {
              start: start,
              end: end
            }
          };
        }

        let trackedBy;
        try {
          trackedBy = await getUserName();
        } catch (e) {
          trackedBy = undefined;
        }
        if (trackedBy) {
          body = { ...body, updatedBy: trackedBy };
        }

        // callAPI never settles on failure, so let the error callback end the wait.
        let signalApiError;
        const apiFailed = new Promise((_, reject) => {
          signalApiError = reject;
        });

        requestIssued = true;
        trackIntoFCLM = await promiseWithTimeout(
          Promise.race([
            apis['LABOR_TRACKING'](
              {
                body: body
              },
              () => {
                showTrackingError(TRACK_FAILED_MSG);
                signalApiError(new Error('FCLM tracking request failed'));
              }
            ),
            apiFailed
          ]),
          30000,
          'FCLM tracking timed out'
        );
        await sleep(2000);
        trackingSucceeded = true;
      } catch (e) {
        showTrackingError(requestIssued ? TRACK_UNKNOWN_MSG : TRACK_FAILED_MSG);
        console.error('FCLM tracking error:', e);
      }

      if (trackingSucceeded) {
        try {
          if (trackIntoFCLM && trackIntoFCLM.laborTrackingStatus) {
            setIsFCLMTrack(setFCLMTrackingEligibility(trackIntoFCLM.laborTrackingStatus));
          } else {
            setIsFCLMTrack(false);
          }

          if (trackIntoFCLM && trackIntoFCLM.associateTrackingStatus) {
            const updatedAssociateInfo = { ...shiftAllocationModel.associateInfo };

            const timeWindowAllocations = cachedResponse.output.resourceAllocationOutput.timeWindowAllocations || [];
            const parsedShift = selectedShift ? JSON.parse(selectedShift) : null;
            const matchingTimeWindow = timeWindowAllocations.find((twa) => {
              const attrs = twa.timeWindowAttrs && twa.timeWindowAttrs[0];
              const timeWindow = twa.timeWindow;
              return (
                attrs &&
                attrs.cycle === selectedCycle &&
                attrs.phase === checkedPhase &&
                timeWindow &&
                parsedShift &&
                timeWindow.start === parsedShift.start &&
                timeWindow.end === parsedShift.end
              );
            });

            const resourceAllocations =
              (matchingTimeWindow &&
                matchingTimeWindow.allocationBoard &&
                matchingTimeWindow.allocationBoard.resourceAllocations) ||
              [];

            Object.entries(trackIntoFCLM.associateTrackingStatus).forEach(([associateId, status]) => {
              if (status === 'SUCCESSFUL' && updatedAssociateInfo[associateId]) {
                const resourceAllocation = resourceAllocations.find((ra) => ra.resourceId === associateId);
                const assignedProcess =
                  resourceAllocation &&
                  resourceAllocation.allocationAttrs &&
                  resourceAllocation.allocationAttrs.process;

                updatedAssociateInfo[associateId] = {
                  ...updatedAssociateInfo[associateId],
                  trackingPath: assignedProcess || null
                };
              }
            });
            shiftAllocationDispatch({
              type: 'SET_MODEL',
              payload: { associateInfo: updatedAssociateInfo }
            });
          }
        } catch (e) {
          console.error('FCLM tracking post-processing failed:', e);
        }
      }
    } finally {
      setIsLoading(false);
    }

    // Refresh enrichment AFTER unlocking the UI so the board is usable while this
    // best-effort refresh runs. Wrap with promiseWithTimeout since apis.callAPI
    // never rejects on non-200 (the promise would hang forever).
    try {
      if (trackIntoFCLM && trackIntoFCLM.laborTrackingStatus) {
        await promiseWithTimeout(pollEnrichmentOnce(), 10000, 'Enrichment refresh timed out');
      }
    } catch (refreshError) {
      console.error('Enrichment refresh after FCLM tracking failed:', refreshError);
    } finally {
      // Released last so the button cannot re-arm before the status bar reflects this track.
      setIsFCLMTracking(false);
      isTrackingRef.current = false;
    }
  };

  // Move associates to bench. If moveAllToBench is false, only ClockedOut associates are moved.
  const onClickRemoveAllocations = (moveAllToBench) => {
    // Publish appropriate metric based on which button was clicked
    if (moveAllToBench) {
      publishLaborAllocationCountMetric(LABOR_ALLOCATION_METRICS.REMOVE_ALL_ASSOCIATES);
    } else {
      publishLaborAllocationCountMetric(LABOR_ALLOCATION_METRICS.REMOVE_CLOCKED_OUT_ASSOCIATES);
    }

    if (moveAssociatesAllocationsToBench(shiftAllocationModel, shiftAllocationDispatch, moveAllToBench)) {
      setIsBoardUpdated(true);
    }
  };

  const onClickRerun = useCallback(async () => {
    publishLaborAllocationCountMetric(LABOR_ALLOCATION_METRICS.RERUN_ALLOCATION_PLAN);

    setLoadingMsg('Rerunning the Shift Allocation Plan');
    setIsLoading(true);
    setIsMissingPreShiftPlan(false);

    const newPlan = await apis['CREATE_PLAN'](
      {
        body: {
          nodeId: getStationCode(),
          ofdDate: date,
          planType: PLANS.PRE_SHIFT,
          planVersion: PLAN_VERSIONS.LABOR_ALLOCATION_V2
        }
      },
      () => setIsMissingPreShiftPlan(true)
    );
    setIsLoading(false);
    setCurrPlan(newPlan.planId);
    setSelectedShift(null);
    setIsFCLMTrack(setFCLMTrackingEligibility(newPlan.laborTrackingStatus));
    setIsBoardUpdated(false);
  }, [setCurrPlan, date]);

  const onClickSavePlan = useCallback(async () => {
    publishLaborAllocationCountMetric(LABOR_ALLOCATION_METRICS.SAVE_ALLOCATION_BOARD);

    const preparedModel = prepareModelForBackend(cachedResponse, {
      ...storedBoardAllocations,
      [shiftAllocationModel.boardAllocationKey]: {
        processInfo: shiftAllocationModel.processInfo,
        associateInfo: shiftAllocationModel.associateInfo,
        boardEntries: shiftAllocationModel.boardEntries,
        clusterDefinitions: shiftAllocationModel.clusterDefinitions
      }
    });

    setLoadingMsg('Updating the Shift Allocation Board');
    setIsLoading(true);
    setIsError(false);
    let newPlan = await apis['CREATE_PLAN'](
      {
        body: {
          nodeId: getStationCode(),
          ofdDate: date,
          planType: preparedModel.planType,
          planVersion: PLAN_VERSIONS.LABOR_ALLOCATION_V2,
          dependentPlanId: preparedModel.planId,
          input: preparedModel.input,
          outputOverride: preparedModel.output
        }
      },
      () => displayError('Shift Allocation Plan override failed, please try again')
    );
    setCurrPlan(newPlan.planId);
    setSelectedShift(null);
    setIsLoading(false);
    setIsBoardUpdated(false);
    setIsFCLMTrack(setFCLMTrackingEligibility(newPlan.laborTrackingStatus));
  }, [date, storedBoardAllocations, shiftAllocationModel, setCurrPlan]);

  const onDragEnd = (result, provided) => {
    // Bench associates may be rendered under multiple shift-start headers with a
    // "alias#@#start" draggableId to keep ids unique. Strip the suffix so all
    // downstream handlers receive the clean associate alias.
    if (result && result.draggableId && result.draggableId.includes('#@#')) {
      result = { ...result, draggableId: result.draggableId.split('#@#')[0] };
    }
    console.log('on DragEnd result-> ', result);
    console.log('on DragEnd provided-> ', provided);

    applyAssociateDrop(result, provided);
    fixStylesOnDragEnd(result, provided, shiftAllocationModel.processInfo);
  };

  const onDragUpdate = (update, provided) => {
    fixStylesOnDragUpdate(update, provided);
  };

  if (isLoading) return <LoadingView loadingMsg={loadingMsg} />;
  if (isError) return <ErrorSection loadingMsg={errorMsg} />;

  return (
    <Row alignmentVertical={'top'} width="100%" spacingInset="400" spacing="200">
      <DragDropContext onDragUpdate={onDragUpdate} onDragEnd={onDragEnd}>
        <Column width={'100%'} height={'100%'} spacingInset="none">
          <Row alignmentHorizontal="justify" wrap="wrap">
            <Box spacingInset="10" minHeight={10}>
              <Row>
                <AddTooltip size={TEXTS.H1}>Labor Board</AddTooltip>
                <Text type={TEXTS.T2} alignment="Left">
                  <b>Date</b>
                </Text>
                <DatePicker
                  value={date}
                  onChange={setDate}
                  monthsInView={1}
                  size="small"
                  disabledDates={(d) => !availableDates.includes(d)}
                  width={175}
                />
              </Row>
              {cachedResponse ? (
                <Text type={TEXTS.T4} color="secondary">
                  Plan ID: {cachedResponse.planId}
                  <CopyToClipboardButton value={cachedResponse.planId} />, Created By: {cachedResponse.lastUpdatedBy},
                  Last updated: {parseEpochToDateString(cachedResponse.updatedAt)}
                </Text>
              ) : (
                <Text></Text>
              )}
            </Box>
            <Row alignmentHorizontal="end" spacing="400" onClick>
              <RadioButton
                checked={checkedPhase === TIME_WINDOW_PHASES.INDUCT_AND_STOW}
                name="color"
                disabled={true}
                value={TIME_WINDOW_PHASES.INDUCT_AND_STOW}>
                Sort
              </RadioButton>
              <RadioButton
                checked={checkedPhase === TIME_WINDOW_PHASES.PICK_AND_STAGE}
                name="color"
                disabled={true}
                value={TIME_WINDOW_PHASES.PICK_AND_STAGE}>
                Pick & Stage
              </RadioButton>
              <Button size="small" type="secondary" onClick={handleAssociateViewButtonClick}>
                Associate View
              </Button>
              <Button size="small" type="tertiary" onClick={onClickJobRotation}>
                Job Rotation
              </Button>
            </Row>
          </Row>

          <Box type="fill" spacingInset="400" width="100%">
            <Row width="100%" wrap="wrap" alignmentHorizontal="justify">
              <Row alignmentHorizontal="start" spacing="300" style={{ flex: '1 1 30%' }}>
                <Text type={TEXTS.T2}>
                  <b>Cycle</b>
                </Text>
                <Select value={selectedCycle} onChange={setSelectedCycle} placeholder="Cycle" size="small">
                  {shiftAllocationModel.availableCycles.map((cycle) => (
                    <SelectOption key={cycle} value={cycle} label={cycle} />
                  ))}
                </Select>
                <Text type={TEXTS.T2}>
                  <b>Period</b>
                </Text>
                <Select value={selectedShift} onChange={setSelectedShift} placeholder="Shift" size="small">
                  {chainWalk(() => shiftAllocationModel.availableCycleShiftsMap.get(selectedCycle), [])
                    .sort((a, b) => a.start.localeCompare(b.start))
                    .map((shift, index) => (
                      <SelectOption key={index} value={JSON.stringify(shift)} label={renderShift(shift)} />
                    ))}
                </Select>
              </Row>
              <Row alignmentHorizontal="center" spacing="300" style={{ flex: '1 1 40%' }}>
                <Tooltip position="top" title={'Remove all associates from the board'}>
                  <Icon tokens={tooltipIcon} />
                </Tooltip>
                <Button size="small" type="tertiary" onClick={() => onClickRemoveAllocations(true)}>
                  Remove All
                </Button>
                <Tooltip position="top" title={'Clean Up Board removes non-clocked in associates'}>
                  <Icon tokens={tooltipIcon} />
                </Tooltip>
                <Button size="small" type="tertiary" onClick={() => onClickRemoveAllocations(false)}>
                  Remove Clocked-Out Associates
                </Button>
                <UpsertInductAndAslLinesModel
                  minLines={0}
                  maxLines={10}
                  adminSettings={adminSettings}
                  shiftAllocationModel={shiftAllocationModel}
                  shiftAllocationDispatch={shiftAllocationDispatch}
                  setIsBoardUpdated={setIsBoardUpdated}
                />
                <UpsertClustersModel
                  adminSettings={adminSettings}
                  shiftAllocationModel={shiftAllocationModel}
                  shiftAllocationDispatch={shiftAllocationDispatch}
                  setIsBoardUpdated={setIsBoardUpdated}
                />
                <Button size="small" type="tertiary" onClick={onClickRerun}>
                  Re-run
                </Button>
              </Row>
              <Row alignmentHorizontal="end" spacing="300" style={{ flex: '1 1 30%' }}>
                <Button size="small" type="tertiary" onClick={onClickOpenAvailableAssociates}>
                  Available Associates
                </Button>
                <Button size="small" type="primary" onClick={onClickSavePlan} disabled={!isBoardUpdated}>
                  Save
                </Button>
                <Button
                  size="small"
                  type="primary"
                  onClick={onClickTrackInFCLM}
                  disabled={!isFCLMTrack || isBoardUpdated || isFCLMTracking}>
                  FCLM Track
                </Button>
              </Row>
            </Row>
          </Box>

          <BoardStatusBar shiftAllocationModel={shiftAllocationModel} cachedResponse={cachedResponse} />

          {currentTab === TIME_WINDOW_PHASES.INDUCT_AND_STOW && (
            <div role="tabpanel" id="tabpanel-planets" aria-labelledby={TIME_WINDOW_PHASES.INDUCT_AND_STOW}>
              <ProcessViewLegend />
              <ProcessPathAllocation
                shiftAllocationModel={shiftAllocationModel}
                shiftAllocationDispatch={shiftAllocationDispatch}
                setIsBoardUpdated={setIsBoardUpdated}
              />
            </div>
          )}
          {currentTab === TIME_WINDOW_PHASES.PICK_AND_STAGE && (
            <div role="tabpanel" id="tabpanel-planets" aria-labelledby={TIME_WINDOW_PHASES.PICK_AND_STAGE}>
              <ProcessViewLegend />
              <ProcessPathAllocation
                shiftAllocationModel={shiftAllocationModel}
                shiftAllocationDispatch={shiftAllocationDispatch}
                setIsBoardUpdated={setIsBoardUpdated}
              />
            </div>
          )}
          <OnDragEndModal
            open={openOnDragEndModal}
            setOpen={setOpenOnDragEndModal}
            result={result}
            provided={provided}
            processInfo={shiftAllocationModel['processInfo']}
            associateInfo={shiftAllocationModel['associateInfo']}
            setIsBoardUpdated={setIsBoardUpdated}
          />
          {isMissingPreShiftPlan && (
            <CustomAlert
              title="A pre-shift plan has not been run for the station at this date, please run a pre-shift plan before running labor allocation."
              disabled="true"
            />
          )}
          {alertMsg && <CustomAlert title={alertMsg} disabled="true" />}
        </Column>

        <Column height={'100%'} spacingInset="none" spacing="none" maxWidth={'25%'}>
          <Sheet type="push" side="end" open={availableAssociatesVisibility} spacingInset="none" spacing="none">
            <AvailableAssociates setOpen={setAvailableAssociatesVisibility} />
          </Sheet>
        </Column>
      </DragDropContext>
      <Modal
        title="Scanned associate"
        open={!!scannedAssociate}
        onClose={() => setScannedAssociate(null)}
        scrollContainer="viewport"
        closeLabel="Close">
        {scannedAssociate && (
          <Column spacing="300">
            <Text type="b200" color="secondary">
              Scanned barcode: {scannedAssociate.barcode}
            </Text>
            <AssignAssociatePanel associate={scannedAssociate.associate} onAssigned={() => setScannedAssociate(null)} />
          </Column>
        )}
      </Modal>
      <Modal
        title="Confirm assignment"
        open={!!pendingSlotAssign}
        onClose={cancelOverrideAssign}
        scrollContainer="viewport"
        closeLabel="Cancel">
        {pendingSlotAssign && (
          <Column spacing="300">
            <Text>
              Assigning{' '}
              {(pendingSlotAssign.associate && pendingSlotAssign.associate.name) || pendingSlotAssign.associate.alias}{' '}
              to {pendingSlotAssign.slot.header || pendingSlotAssign.slot.process} needs an override:
            </Text>
            <ViolationsToggle violations={pendingSlotAssign.violations} onChange={setOverrideToggles} />
            <Row alignmentHorizontal="end" spacing="200">
              <Button type="tertiary" size="small" onClick={cancelOverrideAssign}>
                Cancel
              </Button>
              <Button type="primary" size="small" disabled={!overridesSatisfied} onClick={confirmOverrideAssign}>
                Override & Assign
              </Button>
            </Row>
          </Column>
        )}
      </Modal>
      <Toaster toasts={scanToasts} onCloseToast={onCloseScanToast}>
        {(toast) => (
          <Alert toast={true} onClose={toast.onClose} type={toast.type || 'warning'}>
            {toast.message}
          </Alert>
        )}
      </Toaster>
    </Row>
  );
}

export default ShiftAllocation;
